import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  X,
  ArrowRight,
  SlidersHorizontal,
  Settings as SettingsIcon,
  ShieldAlert,
  FolderCog,
} from 'lucide-react'
import { Badge } from '@jira-clone/ui'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { canManageHrSettings } from '@/features/hr/access'
import {
  SETTINGS_GROUPS,
  type SettingAccess,
  type SettingItem,
} from './settingsConfig'
import { cn, cardClassName } from '@jira-clone/ui'

export default function SettingsOverview() {
  const { user } = useAuth()
  const isSuperAdmin = isSuperAdminOrOwner(user?.role)
  const canSeeHr = canManageHrSettings(user)
  const isAdmin = isSuperAdmin || canSeeHr

  const [activeTab, setActiveTab] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Filter groups according to user role
  const accessibleGroups = useMemo(() => {
    // An item can override its group's access (Device Allocation is open to HR Admins inside Assets & Media).
    const allowed = (access: SettingAccess) => (access === 'superadmin' ? isSuperAdmin : access === 'hr' ? canSeeHr : true)
    return SETTINGS_GROUPS
      .map((group) => ({ ...group, items: group.items.filter((item) => allowed(item.access ?? group.access)) }))
      .filter((group) => group.items.length > 0)
  }, [isSuperAdmin, canSeeHr])

  // All accessible items
  const accessibleItems = useMemo(() => {
    return accessibleGroups.flatMap((group) => group.items)
  }, [accessibleGroups])

  // Filter items by search query
  const searchFilteredItems = useMemo(() => {
    if (!searchQuery.trim()) return accessibleItems
    const query = searchQuery.toLowerCase().trim()
    return accessibleItems.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query) ||
        item.badge.toLowerCase().includes(query)
    )
  }, [accessibleItems, searchQuery])

  // Group items for display
  const displayedGroups = useMemo(() => {
    return accessibleGroups
      .map((group) => {
        if (activeTab !== 'all' && group.key !== activeTab) {
          return null
        }

        const filteredGroupItems = group.items.filter((item) =>
          searchFilteredItems.some((si) => si.id === item.id)
        )

        if (filteredGroupItems.length === 0) return null

        return {
          ...group,
          items: filteredGroupItems,
        }
      })
      .filter(Boolean) as (typeof accessibleGroups[number])[]
  }, [accessibleGroups, activeTab, searchFilteredItems])

  const totalResultsCount = searchFilteredItems.length

  return (
    <div className="space-y-6 w-full max-w-[1800px] mx-auto pb-12">
      {/* Header matching Company People page */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-primary/10 text-primary">
              <SettingsIcon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Settings & Configuration
              </h1>
              <p className="text-sm text-muted-foreground">
                Manage personal account preferences, workspace security rules, company assets, and centralized master data tables
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={cn(cardClassName(), 'px-3 py-1.5 text-center')}>
            <p className="text-[10px] text-muted-foreground uppercase font-bold">Access Level</p>
            <p className="text-sm font-bold text-foreground">
              {isAdmin ? 'Administrator' : 'Standard'}
            </p>
          </div>
          <div className="px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50/50 dark:bg-purple-950/30 text-center">
            <p className="text-[10px] text-purple-600 dark:text-purple-400 uppercase font-bold">Modules</p>
            <p className="text-lg font-bold text-purple-700 dark:text-purple-300 font-mono">
              {accessibleItems.length}
            </p>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="space-y-3">
        <div className={cn(cardClassName(), 'flex items-center gap-4 p-3')}>
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search settings (e.g. Taxes, Bank Details, Roles, Email, HSN)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 h-9 rounded-lg border border-input bg-background px-3 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
          <button
            onClick={() => setActiveTab('all')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              activeTab === 'all'
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>All Settings</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                activeTab === 'all'
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {accessibleItems.length}
            </span>
          </button>

          {accessibleGroups.map((group) => {
            const isActive = activeTab === group.key
            return (
              <button
                key={group.key}
                onClick={() => setActiveTab(group.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                }`}
              >
                <span>{group.label}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                    isActive
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {group.items.length}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Results Counter if search active */}
      {searchQuery.trim() && (
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>
            Found <strong className="text-foreground">{totalResultsCount}</strong> settings matching "{searchQuery}"
          </span>
          <button
            onClick={() => setSearchQuery('')}
            className="text-primary hover:underline font-medium"
          >
            Reset search
          </button>
        </div>
      )}

      {/* Empty Search State */}
      {displayedGroups.length === 0 ? (
        <div className={cn(cardClassName(), 'p-12 text-center')}>
          <SettingsIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <h3 className="text-sm font-semibold text-foreground">No settings found</h3>
          <p className="text-xs text-muted-foreground mt-1">
            We couldn't find any configuration module matching "{searchQuery}".
          </p>
          <button
            onClick={() => {
              setSearchQuery('')
              setActiveTab('all')
            }}
            className="mt-4 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {displayedGroups.map((group) => (
            <div key={group.key} className="space-y-3">
              {/* Group Header matching PeoplePage section format */}
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <FolderCog className="h-4 w-4 text-primary" />
                <span>{group.label}</span>
                <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
                  {group.items.length} {group.items.length === 1 ? 'module' : 'modules'}
                </Badge>
              </div>

              {/* Grid of Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {group.items.map((item) => (
                  <SettingCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Non-Admin Notice if applicable */}
      {!isAdmin && (
        <div className={cn(cardClassName(), 'p-4 flex items-start gap-3')}>
          <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Notice: </span>
            Administrative configurations (Departments, Roles, Assets, Master Data and HR) need an administrator. Contact your organization administrator to request higher permissions.
          </div>
        </div>
      )}
    </div>
  )
}

function SettingCard({ item }: { item: SettingItem }) {
  const Icon = item.icon

  return (
    <Link
      to={item.href}
      className={cn(cardClassName({ interactive: true }), 'group relative flex flex-col justify-between p-4 hover:border-primary/50 transition-all duration-200', item.hoverBorder)}
    >
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105 shadow-2xs ${item.iconBg}`}
          >
            <Icon className="w-4 h-4" />
          </div>

          <div className="w-7 h-7 rounded-full bg-muted/60 text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center transition-all duration-200 shadow-2xs">
            <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </div>
        </div>

        <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
          {item.label}
        </h3>

        <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">
          {item.description}
        </p>
      </div>

      <div className="mt-3 pt-2.5 border-t border-border/50 flex items-center justify-between text-[11px]">
        <span className="font-medium text-muted-foreground/80 px-1.5 py-0.5 rounded bg-muted/60 text-[10px]">
          {item.badge}
        </span>
        <span className="text-[11px] font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          Open →
        </span>
      </div>
    </Link>
  )
}
